/**
 * The QRkey API client: openapi-fetch typed by the generated `paths`, over a fetch
 * wrapper that implements the API's conventions (05-api.md):
 *
 * - `Authorization: Bearer <access token>` when signed in, and `X-Terminal-Id` when the
 *   session is bound to a terminal (the API checks it matches);
 * - `Idempotency-Key` (a UUIDv7) on every POST/PUT/PATCH/DELETE that has none, kept
 *   across the client's own retry so a write is never applied twice (CLAUDE.md rule 3);
 *   and when a write's answer was lost (no connection, timeout, 5xx), the same write
 *   sent again (same method, URL and body) reuses its key, so the server replays the
 *   first outcome instead of applying it again;
 * - `X-Request-Id` on every request, for tracing a failure to the server's logs;
 * - refresh before the access token expires and on a 401, one refresh at a time
 *   (single-flight in this tab, and across tabs where the Web Locks API exists),
 *   because refresh tokens are single use and a second use ends the session;
 * - problem+json answers become `ApiError`s (see `unwrap`), lost connections
 *   `NetworkError`s.
 */
import { newId } from '@qrkey/contracts/ids';
import createClient, { type Client } from 'openapi-fetch';
import { apiErrorFromResponse, NetworkError, type ApiError } from './errors.js';
import type { paths } from './generated/schema.js';
import type { TokenPair } from './types.js';

/** Where the signed-in session lives (localStorage in the apps; memory in tests). */
export interface TokenStore {
  get(): TokenPair | null;
  set(tokens: TokenPair | null): void;
}

export type SessionEndReason = 'refresh_failed' | 'unauthenticated';

export interface ApiClientOptions {
  /** '' = same origin (the Vite proxy in development, the CDN/gateway in production). */
  baseUrl?: string;
  tokens: TokenStore;
  /**
   * The registered terminal's device secret: a terminal-bound session refreshes only
   * with it (docs/dev.md, "Tokens").
   */
  terminalSecret?: () => string | null | undefined;
  /** The session is gone (refresh refused, or a 401 that refreshing cannot fix). */
  onSessionEnded?: (reason: SessionEndReason) => void;
  fetch?: (request: Request) => Promise<Response>;
  now?: () => number;
  /** Refresh this long before the access token expires (default 30 s). */
  refreshLeewayMs?: number;
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
/** Writes whose outcome is unknown, remembered (by fingerprint) at most this many. */
const MAX_UNCERTAIN = 100;

/** The write as the server's request hash sees it: method, URL and JSON body. */
async function writeFingerprint(request: Request): Promise<string | null> {
  const type = request.headers.get('content-type') ?? '';
  // Multipart bodies carry a random boundary: they never match, so never remember them.
  if (request.body !== null && !type.includes('json')) return null;
  const body = request.body === null ? '' : await request.clone().text();
  return `${request.method} ${request.url}\n${body}`;
}

/** No answer, or one that does not say whether the write was applied. */
function outcomeUnknown(response: Response | null): boolean {
  return response === null || response.status === 408 || response.status >= 500;
}
const REFRESH_PATH = '/v1/auth/refresh';
const LOCK_NAME = 'qrkey-auth-refresh';

export interface ApiClient {
  /** Typed client: `api.client.POST('/v1/auth/login', { body })`. */
  readonly client: Client<paths>;
  /** The wrapped fetch, for calls the generated paths do not cover yet. */
  readonly fetch: (request: Request) => Promise<Response>;
  readonly baseUrl: string;
  /** Refresh now (single-flight). Resolves to the new tokens, or null when signed out. */
  refresh(): Promise<TokenPair | null>;
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  // Request needs an absolute URL outside a browser document; '' means this origin.
  const origin = typeof location === 'undefined' ? '' : location.origin;
  const baseUrl = (options.baseUrl || origin).replace(/\/$/, '');
  const rawFetch = options.fetch ?? ((r: Request) => globalThis.fetch(r));
  const now = options.now ?? Date.now;
  const leeway = options.refreshLeewayMs ?? 30_000;
  let inflight: Promise<TokenPair | null> | null = null;
  // Idempotency-Key per write fingerprint whose last attempt got no definite answer.
  const uncertain = new Map<string, string>();

  async function send(request: Request): Promise<Response> {
    try {
      return await rawFetch(request);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      throw new NetworkError(error);
    }
  }

  async function doRefresh(spent: TokenPair): Promise<TokenPair | null> {
    // Another tab may have refreshed while we waited for the lock.
    const current = options.tokens.get();
    if (!current) return null;
    if (current.refreshToken !== spent.refreshToken) return current;
    const secret = current.session.terminalId ? options.terminalSecret?.() : undefined;
    const response = await send(
      new Request(`${baseUrl}${REFRESH_PATH}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'x-request-id': newId() },
        body: JSON.stringify({
          refreshToken: current.refreshToken,
          ...(secret ? { terminalSecret: secret } : {}),
        }),
      }),
    );
    if (response.ok) {
      const next = (await response.json()) as TokenPair;
      options.tokens.set(next);
      return next;
    }
    if (response.status === 401 || response.status === 403) {
      // Ended, expired, reused or the terminal was blocked: this session is over.
      options.tokens.set(null);
      options.onSessionEnded?.('refresh_failed');
      return null;
    }
    // 5xx / 429: keep the session (a till must survive a blip) and report the failure.
    throw await apiErrorFromResponse(response);
  }

  function refresh(): Promise<TokenPair | null> {
    if (inflight) return inflight;
    const spent = options.tokens.get();
    if (!spent) return Promise.resolve(null);
    const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
    const run = locks ? locks.request(LOCK_NAME, () => doRefresh(spent)) : doRefresh(spent);
    inflight = (run as Promise<TokenPair | null>).finally(() => {
      inflight = null;
    });
    return inflight;
  }

  function decorate(request: Request, tokens: TokenPair | null): Request {
    const headers = new Headers(request.headers);
    if (!headers.has('x-request-id')) headers.set('x-request-id', newId());
    if (!SAFE_METHODS.has(request.method) && !headers.has('idempotency-key')) {
      headers.set('idempotency-key', newId());
    }
    if (tokens && !headers.has('authorization')) {
      headers.set('authorization', `Bearer ${tokens.accessToken}`);
      if (tokens.session.terminalId) headers.set('x-terminal-id', tokens.session.terminalId);
    }
    return new Request(request, { headers });
  }

  async function authFetch(original: Request): Promise<Response> {
    if (SAFE_METHODS.has(original.method) || original.headers.has('idempotency-key')) {
      return authFetchKeyed(original);
    }
    const fp = await writeFingerprint(original);
    const key = (fp !== null ? uncertain.get(fp) : undefined) ?? newId();
    const headers = new Headers(original.headers);
    headers.set('idempotency-key', key);
    const input = new Request(original, { headers });
    let response: Response | null = null;
    try {
      response = await authFetchKeyed(input);
      return response;
    } finally {
      if (fp !== null) {
        uncertain.delete(fp);
        if (outcomeUnknown(response)) {
          uncertain.set(fp, key);
          if (uncertain.size > MAX_UNCERTAIN) {
            const oldest = uncertain.keys().next().value;
            if (oldest !== undefined) uncertain.delete(oldest);
          }
        }
      }
    }
  }

  async function authFetchKeyed(input: Request): Promise<Response> {
    let tokens = options.tokens.get();
    const isRefresh = new URL(input.url, 'http://x').pathname.endsWith(REFRESH_PATH);
    if (tokens && !isRefresh && Date.parse(tokens.accessTokenExpiresAt) - leeway <= now()) {
      try {
        tokens = await refresh();
      } catch {
        // Could not refresh right now; try with the current token.
      }
    }
    const request = decorate(input, tokens);
    // Keep an unsent copy (same headers, incl. Idempotency-Key) for one retry after a refresh.
    const retry = tokens ? request.clone() : null;
    const response = await send(request);
    if (response.status !== 401 || !tokens || !retry || isRefresh) return response;

    const problemCode = await response
      .clone()
      .json()
      .then((b: { code?: unknown }) => b?.code)
      .catch(() => undefined);
    if (problemCode !== undefined && problemCode !== 'UNAUTHENTICATED') return response;

    const fresh = await refresh();
    if (!fresh) return response;
    const headers = new Headers(retry.headers);
    headers.set('authorization', `Bearer ${fresh.accessToken}`);
    const again = await send(new Request(retry, { headers }));
    if (again.status === 401) {
      options.tokens.set(null);
      options.onSessionEnded?.('unauthenticated');
    }
    return again;
  }

  const client = createClient<paths>({ baseUrl, fetch: authFetch });
  return { client, fetch: authFetch, baseUrl, refresh };
}

type FetchResult = { data?: unknown; error?: unknown; response: Response };

/**
 * The data of an openapi-fetch result, or an `ApiError` thrown for a problem answer:
 * `const me = await unwrap(api.client.GET('/v1/me'))`.
 */
export async function unwrap<R extends FetchResult>(
  result: Promise<R> | R,
): Promise<Exclude<R['data'], undefined>> {
  const r = await result;
  if (!r.response.ok || r.error !== undefined) {
    throw (await apiErrorFromResponse(r.response, r.error)) satisfies ApiError;
  }
  return r.data as Exclude<R['data'], undefined>;
}
